import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/auth';

const api = axios.create({ baseURL: '/api', timeout: 10000 });
const authApi = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

function timeAgo(ms) {
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${d}d ago`;
}

// Bell button shown in sidebar header — exported separately for use in App
export function BellButton({ unread, onClick }) {
  return (
    <button
      onClick={onClick}
      title="Notifications"
      style={{
        position: 'relative', width: 32, height: 32, borderRadius: '50%',
        background: unread > 0 ? 'var(--accent-dim)' : 'var(--bg-hover)',
        border: `1.5px solid ${unread > 0 ? 'var(--accent)' : 'var(--border)'}`,
        color: unread > 0 ? 'var(--accent)' : 'var(--text-muted)',
        cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s',
      }}
    >
      🔔
      {unread > 0 && (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          background: 'var(--red)', color: '#fff',
          borderRadius: '50%', width: 16, height: 16,
          fontSize: 9, fontWeight: 700, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          border: '2px solid var(--bg-sidebar)',
        }}>
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </button>
  );
}

export default function AlertInbox({ onClose, onUnreadChange }) {
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [tab, setTab] = useState('inbox'); // 'inbox' | 'active'
  const ref = useRef(null);

  const load = async () => {
    try {
      const [notifRes, alertRes] = await Promise.all([
        api.get('/alerts/notifications', authApi()),
        api.get('/alerts', authApi()),
      ]);
      setData(notifRes.data);
      setAlerts(alertRes.data);
      onUnreadChange?.(notifRes.data.unread);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const markAllRead = async () => {
    await api.put('/alerts/notifications/read-all', {}, authApi());
    setData(d => ({ ...d, unread: 0, notifications: d.notifications.map(n => ({ ...n, read_at: Date.now() })) }));
    onUnreadChange?.(0);
  };

  const cancelAlert = async (id) => {
    await api.delete(`/alerts/${id}`, authApi());
    setAlerts(a => a.filter(x => x.id !== id));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 9998, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
      paddingTop: 60, paddingRight: 16,
    }}>
      <div ref={ref} style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 12, width: 380, maxHeight: '80vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Notifications</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {['inbox', 'active'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                background: tab === t ? 'var(--accent)' : 'var(--bg-hover)',
                color: tab === t ? '#fff' : 'var(--text-muted)',
              }}>
                {t === 'inbox' ? `📬 Inbox${data.unread > 0 ? ` (${data.unread})` : ''}` : `🔔 Active Alerts (${alerts.length})`}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', scrollbarWidth: 'none' }}>
          {loading && (
            <div style={{ padding: 20 }}>
              {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 56, borderRadius: 8, marginBottom: 10 }} />)}
            </div>
          )}

          {/* INBOX TAB */}
          {!loading && tab === 'inbox' && (
            <>
              {data.unread > 0 && (
                <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    Mark all read
                  </button>
                </div>
              )}
              {data.notifications.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No alerts triggered yet.<br />Set a price alert on any stock.
                </div>
              )}
              {data.notifications.map(n => (
                <div key={n.id} style={{
                  padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)',
                  background: !n.read_at ? 'var(--accent-dim)' : 'transparent',
                  transition: 'background 0.2s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>{n.symbol}</span>
                        <span style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                          background: n.condition === 'above' ? 'var(--green-dim)' : 'var(--red-dim)',
                          color: n.condition === 'above' ? 'var(--green)' : 'var(--red)',
                        }}>{n.condition === 'above' ? '↑ ABOVE' : '↓ BELOW'}</span>
                        {!n.read_at && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{n.message}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{timeAgo(n.triggered_at)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* ACTIVE ALERTS TAB */}
          {!loading && tab === 'active' && (
            <>
              {alerts.length === 0 && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  No active alerts.<br />Use the 🔔 button on any stock.
                </div>
              )}
              {alerts.map(a => (
                <div key={a.id} style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, fontSize: 13 }}>{a.symbol}</span>
                      <span style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 700,
                        background: a.condition === 'above' ? 'var(--green-dim)' : 'var(--red-dim)',
                        color: a.condition === 'above' ? 'var(--green)' : 'var(--red)',
                      }}>{a.condition === 'above' ? '↑ ABOVE' : '↓ BELOW'}</span>
                    </div>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                      ${a.target_price.toFixed(2)}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{a.name}</div>
                  </div>
                  <button
                    onClick={() => cancelAlert(a.id)}
                    title="Cancel alert"
                    style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}
                  >✕</button>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
